import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, MessageCircle, ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white fill-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">TicketDesk</h1>
                <p className="text-xs text-gray-500">Customer Support Platform</p>
              </div>
            </Link>

            <Link
              to="/"
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <FileText className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600">Last updated: {currentDate}</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12 space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") govern your access to and use of TicketDesk's customer support platform and related services (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If you do not agree to these Terms, you may not access or use the Service. If you are using the Service on behalf of an organization, you are agreeing to these Terms for that organization and representing that you have the authority to bind that organization to these Terms.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Service Description</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              TicketDesk provides a cloud-based customer support platform that enables businesses to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Manage customer support tickets from multiple channels (email, Facebook Messenger, chat widget)</li>
              <li>Track and respond to customer reviews from various platforms</li>
              <li>Collaborate with team members on customer issues</li>
              <li>Analyze support metrics and performance</li>
              <li>Integrate with third-party services (Gmail, Facebook, review platforms)</li>
            </ul>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Account Registration and Security</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.1 Account Creation</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              To use the Service, you must create an account by providing accurate and complete information. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and account</li>
              <li>Be responsible for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized access or security breach</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Account Eligibility</h3>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 16 years old to use the Service. By using the Service, you represent and warrant that you meet this age requirement.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Acceptable Use Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Send spam, unsolicited messages, or engage in phishing</li>
              <li>Distribute malware, viruses, or harmful code</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Scrape, copy, or reverse engineer the Service</li>
              <li>Use the Service for illegal activities or fraudulent purposes</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Impersonate any person or entity</li>
              <li>Collect or store personal data of others without consent</li>
            </ul>
          </section>

          {/* Subscription Plans */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription Plans and Billing</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.1 Subscription Tiers</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              TicketDesk offers multiple subscription plans with varying features and usage limits. Details of each plan are available on our pricing page.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Payment Terms</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Subscriptions are billed monthly or annually in advance</li>
              <li>Payments are processed securely through Stripe</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>You authorize us to charge your payment method for recurring subscription fees</li>
              <li>Failure to pay may result in service suspension or termination</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 Free Trial</h3>
            <p className="text-gray-700 leading-relaxed">
              We may offer a free trial period for new users. At the end of the trial, your subscription will automatically convert to a paid plan unless you cancel before the trial ends.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.4 Plan Changes and Cancellation</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>You may upgrade, downgrade, or cancel your subscription at any time</li>
              <li>Changes take effect at the start of the next billing cycle</li>
              <li>Cancellations must be made before the renewal date to avoid charges</li>
              <li>No refunds are provided for partial months or unused features</li>
            </ul>
          </section>

          {/* Usage Limits */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Usage Limits and Restrictions</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Each subscription plan includes specific usage limits for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Number of support tickets per month</li>
              <li>Number of reviews tracked per month</li>
              <li>Number of team members</li>
              <li>Number of workspaces</li>
              <li>Storage capacity</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We reserve the right to enforce these limits and may suspend service if limits are significantly exceeded. You may upgrade your plan to increase limits.
            </p>
          </section>

          {/* Data and Content */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Data and Content</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.1 Your Responsibility</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              You retain ownership of all data and content you submit to the Service ("Your Content"). You are solely responsible for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The accuracy, legality, and appropriateness of Your Content</li>
              <li>Obtaining necessary rights and permissions for Your Content</li>
              <li>Compliance with data protection laws regarding customer data</li>
              <li>Backing up Your Content (we provide backup services but are not liable for data loss)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.2 License to TicketDesk</h3>
            <p className="text-gray-700 leading-relaxed">
              You grant TicketDesk a non-exclusive, worldwide, royalty-free license to use, store, process, and display Your Content solely to provide and improve the Service. This license terminates when you delete Your Content or terminate your account.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.3 Data Security</h3>
            <p className="text-gray-700 leading-relaxed">
              We implement industry-standard security measures to protect Your Content. However, you acknowledge that no system is completely secure and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Third-Party Integrations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Third-Party Integrations</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              The Service integrates with third-party services (Gmail, Facebook, review platforms). By using these integrations, you agree that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>You comply with the terms and policies of those third-party services</li>
              <li>We are not responsible for the availability or functionality of third-party services</li>
              <li>Third-party integrations may be modified or discontinued at any time</li>
              <li>You grant necessary permissions for us to access and use data from integrated services</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property Rights</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">8.1 TicketDesk IP</h3>
            <p className="text-gray-700 leading-relaxed">
              The Service, including all software, designs, graphics, logos, and trademarks, is owned by TicketDesk and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">8.2 Feedback</h3>
            <p className="text-gray-700 leading-relaxed">
              If you provide feedback, suggestions, or ideas about the Service, you grant us an unrestricted, perpetual license to use and incorporate such feedback without compensation or attribution.
            </p>
          </section>

          {/* Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Your privacy is important to us. Our <Link to="/privacy" className="text-purple-600 hover:text-purple-700 font-medium">Privacy Policy</Link> explains how we collect, use, and protect your information. By using the Service, you agree to our Privacy Policy.
            </p>
          </section>

          {/* Service Availability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Service Availability and Modifications</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.1 Availability</h3>
            <p className="text-gray-700 leading-relaxed">
              We strive to provide reliable service but do not guarantee uninterrupted or error-free operation. We may perform maintenance, updates, or experience downtime without prior notice.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.2 Modifications</h3>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time. We will provide reasonable notice of material changes when possible.
            </p>
          </section>

          {/* Warranties and Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Warranties and Disclaimers</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
              <p className="text-gray-700 leading-relaxed mb-3">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES INCLUDING:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>MERCHANTABILITY</li>
                <li>FITNESS FOR A PARTICULAR PURPOSE</li>
                <li>NON-INFRINGEMENT</li>
                <li>ACCURACY, RELIABILITY, OR AVAILABILITY</li>
                <li>ERROR-FREE OR UNINTERRUPTED OPERATION</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                You use the Service at your own risk.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Limitation of Liability</h2>
            <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded">
              <p className="text-gray-700 leading-relaxed mb-3">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, TICKETDESK SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                <li>LOSS OF PROFITS, REVENUE, DATA, OR USE</li>
                <li>BUSINESS INTERRUPTION OR SYSTEM FAILURES</li>
                <li>UNAUTHORIZED ACCESS TO YOUR DATA</li>
                <li>THIRD-PARTY CONDUCT OR CONTENT</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRIOR TO THE EVENT GIVING RISE TO THE CLAIM.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless TicketDesk and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney's fees) arising from:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your Content</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Termination</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">14.1 By You</h3>
            <p className="text-gray-700 leading-relaxed">
              You may terminate your account at any time through your account settings or by contacting support.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">14.2 By TicketDesk</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may suspend or terminate your access to the Service immediately, without notice, if:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>You violate these Terms</li>
              <li>You engage in fraudulent or illegal activity</li>
              <li>Your account poses a security risk</li>
              <li>You fail to pay subscription fees</li>
              <li>Required by law or regulatory authority</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">14.3 Effect of Termination</h3>
            <p className="text-gray-700 leading-relaxed">
              Upon termination, your right to use the Service ends immediately. We will delete Your Content within 90 days unless required by law to retain it. Provisions of these Terms that by their nature should survive termination will remain in effect.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Dispute Resolution</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">15.1 Informal Resolution</h3>
            <p className="text-gray-700 leading-relaxed">
              Before filing a claim, you agree to contact us at <a href="mailto:legal@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">legal@ticketdesk.app</a> to attempt to resolve the dispute informally.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">15.2 Arbitration</h3>
            <p className="text-gray-700 leading-relaxed">
              If informal resolution fails, disputes shall be resolved through binding arbitration rather than in court, except for disputes related to intellectual property or injunctive relief.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">15.3 Class Action Waiver</h3>
            <p className="text-gray-700 leading-relaxed">
              You agree to resolve disputes on an individual basis only and waive the right to participate in class actions or representative proceedings.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Governing Law and Jurisdiction</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of [Your Jurisdiction], without regard to conflict of law principles. Any legal action must be brought in the courts located in [Your Jurisdiction].
            </p>
          </section>

          {/* General Provisions */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. General Provisions</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">17.1 Entire Agreement</h3>
            <p className="text-gray-700 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and TicketDesk regarding the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">17.2 Severability</h3>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will remain in full force and effect.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">17.3 Waiver</h3>
            <p className="text-gray-700 leading-relaxed">
              No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">17.4 Assignment</h3>
            <p className="text-gray-700 leading-relaxed">
              You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">17.5 Force Majeure</h3>
            <p className="text-gray-700 leading-relaxed">
              We are not liable for failure to perform due to circumstances beyond our reasonable control, including natural disasters, war, terrorism, strikes, or internet failures.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these Terms from time to time. We will notify you of material changes by email or through the Service. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <p className="text-gray-900 font-semibold mb-2">TicketDesk Legal Team</p>
              <p className="text-gray-700">Email: <a href="mailto:legal@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">legal@ticketdesk.app</a></p>
              <p className="text-gray-700">Support: <a href="mailto:support@ticketdesk.app" className="text-purple-600 hover:text-purple-700 font-medium">support@ticketdesk.app</a></p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="bg-gray-50 rounded-lg p-6 border-l-4 border-purple-600">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Acknowledgment</h3>
            <p className="text-gray-700 leading-relaxed">
              By using TicketDesk, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            These Terms of Service are effective as of {currentDate}.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Return to Homepage
          </Link>
        </div>
      </div>

      {/* Footer Section */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white fill-white" />
              </div>
              <span className="text-gray-600">© 2025 TicketDesk. All rights reserved.</span>
            </div>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-600 hover:text-purple-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-600 hover:text-purple-600 transition-colors">Terms of Service</Link>
              <a href="mailto:support@ticketdesk.app" className="text-gray-600 hover:text-purple-600 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
